import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { walletService } from "../services/wallet";
import type { Transaction } from "../services/wallet";
import { useState } from "react";

export default function WalletPage() {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  const [externalAddress, setExternalAddress] = useState("");
  const [showAddressForm, setShowAddressForm] = useState(false);

  const { data: walletData, isLoading: walletLoading, error: walletError } = useQuery({
    queryKey: ["wallet"],
    queryFn: () => walletService.get(),
  });

  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ["transactions"],
    queryFn: () => walletService.getTransactions(),
  });

  const depositMutation = useMutation({
    mutationFn: walletService.deposit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallet"] });
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      setAmount("");
    },
    onError: (err: any) => {
      alert(err.response?.data?.error || "Failed to deposit");
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: walletService.updateAddress,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallet"] });
      setShowAddressForm(false);
      setExternalAddress("");
      alert("Wallet address updated successfully!");
    },
    onError: (err: any) => {
      alert(err.response?.data?.error || "Failed to update wallet address");
    },
  });

  if (walletLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading wallet...</div>
      </div>
    );
  }

  if (walletError) {
    return (
      <div className="card">
        <p className="text-red-400">Error loading wallet: {(walletError as Error).message}</p>
      </div>
    );
  }

  const wallet = walletData?.wallet;
  const transactions = transactionsData?.transactions || [];

  if (!wallet) {
    return (
      <div className="card">
        <p className="text-gray-400">Wallet not found.</p>
      </div>
    );
  }

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case "deposit":
      case "reward":
        return "text-green-400";
      case "withdrawal":
      case "lock":
        return "text-red-400";
      case "refund":
        return "text-blue-400";
      default:
        return "text-gray-400";
    }
  };

  const getTransactionTypeLabel = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-100 mb-8">Wallet</h1>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Total Balance</h3>
          <p className="text-3xl font-bold text-primary-500">{wallet.balance} coins</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Available</h3>
          <p className="text-3xl font-bold text-green-400">{wallet.available_balance} coins</p>
        </div>
        <div className="card">
          <h3 className="text-sm font-medium text-gray-400 mb-1">Locked</h3>
          <p className="text-3xl font-bold text-orange-400">{wallet.locked_balance} coins</p>
        </div>
      </div>

      {/* External Wallet Address */}
      <div className="card mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-100">External Wallet Address</h2>
          {!showAddressForm && (
            <button
              onClick={() => setShowAddressForm(true)}
              className="btn btn-secondary text-sm"
            >
              Update Address
            </button>
          )}
        </div>

        {showAddressForm ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (externalAddress) {
                updateAddressMutation.mutate(externalAddress);
              }
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                New Ethereum Address (0x...)
              </label>
              <input
                type="text"
                className="input w-full"
                placeholder="0x1234567890123456789012345678901234567890"
                value={externalAddress}
                onChange={(e) => setExternalAddress(e.target.value)}
                pattern="^0x[a-fA-F0-9]{40}$"
                title="Must be a valid Ethereum address (42 characters starting with 0x)"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Used for withdrawing funds to your external wallet
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={updateAddressMutation.isPending}
              >
                {updateAddressMutation.isPending ? "Updating..." : "Update Address"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddressForm(false);
                  setExternalAddress("");
                }}
                className="btn btn-secondary"
                disabled={updateAddressMutation.isPending}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div>
            {wallet.external_address ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-400 break-all font-mono bg-dark-800 p-3 rounded">
                  {wallet.external_address}
                </p>
                <p className="text-xs text-gray-500">
                  This address will be used when you withdraw funds.
                </p>
              </div>
            ) : (
              <p className="text-gray-400">
                No external wallet address set. Add one to enable withdrawals.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Deposit Form */}
      <div className="card mb-8">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Deposit Funds (Demo)</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (amount && parseFloat(amount) > 0) {
              depositMutation.mutate(amount);
            }
          }}
          className="flex gap-4"
        >
          <input
            type="number"
            min="0"
            step="0.01"
            className="input flex-1"
            placeholder="Amount to deposit"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <button type="submit" className="btn btn-primary" disabled={depositMutation.isPending}>
            {depositMutation.isPending ? "Depositing..." : "Deposit"}
          </button>
        </form>
      </div>

      {/* Transaction History */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-100 mb-4">Transaction History</h2>

        {transactionsLoading ? (
          <p className="text-gray-400">Loading transactions...</p>
        ) : transactions.length === 0 ? (
          <p className="text-gray-400">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-dark-border">
              <thead className="bg-dark-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Balance After
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-dark-700 divide-y divide-dark-border">
                {transactions.map((tx: Transaction) => (
                  <tr key={tx.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`font-medium ${getTransactionTypeColor(tx.type)}`}>
                        {getTransactionTypeLabel(tx.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`font-medium ${getTransactionTypeColor(tx.type)}`}>
                        {tx.type === "withdrawal" || tx.type === "lock" ? "-" : "+"}{tx.amount}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-200">
                      {tx.balance_after}
                    </td>
                    <td className="px-6 py-4 text-gray-400">
                      {tx.description || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {new Date(tx.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
